/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
*/

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { makeStyles, useTheme, Theme, createStyles } from '@material-ui/core/styles';
import { Popover, Typography, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, IconButton, Box } from '@material-ui/core';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { isValidData, renderSkeleton, getTimeDurationForDate } from '../../../data/utils';
import { ApiCommentsReducer, ApiReducer, ApplicationState, Comment, CreateComment, TablePaginationActionsProps, UserReducer } from '../../../data/interfaces';
import { deleteComment, editComment } from '../../../data/operations';
import * as Constants from '../../../data/constants';
import PrimaryButton from '../../../elements/PrimaryButton';
import EditableTextField from '../../../elements/EditableTextField';
import { CLAIMS_FULL_NAME, CREATED_BY_USER } from '../../../data/constants';

const useStylesTablePagination = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexShrink: 0,
            marginLeft: theme.spacing(2.5),
        },
    }),
);

function TablePaginationActions(props: TablePaginationActionsProps) {
    const classes = useStylesTablePagination();
    const theme = useTheme();
    const { count, page, rowsPerPage, onChangePage } = props;

    const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onChangePage(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onChangePage(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onChangePage(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <div className={classes.root}>
            <IconButton
                data-testid="btn-comments-first-page"
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                data-testid="btn-comments-next-page"
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                data-testid="btn-comments-last-page"
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </div>
    );
}

const useStylesCommentsTable = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            minWidth: 200,
        },
        tableCell: {
            ...theme.custom.apiOverviewPage.tabs.overview.apiCommentCreator,
            paddingRight: theme.spacing(1),
        },
        tableCellContent: {
            paddingRight: theme.spacing(1),
            ...theme.custom.apiOverviewPage.tabs.overview.apiCommentsCellContent
        },
        commentDeleteBtn: {
            ...theme.custom.apiOverviewPage.tabs.overview.apiCommentsDeleteButton
        },
        cmntDelConfirmText: {
            padding: theme.spacing(2),
            ...theme.custom.apiOverviewPage.tabs.overview.apiCommentDeleteConfirmText
        },
        commentDelConfirm: {
            margin: '5px',
        }
    }));

interface ApiCommentsTableProps {
    commentsReducer: ApiCommentsReducer,
    userReducer: UserReducer,
    apiReducer: ApiReducer,
    deleteComment: (apiId: string, organizationId: string, commentId: string) => Promise<void>,
    editComment: (apiId: string, organizationId: string, commentId: string, comment: CreateComment) => Promise<void>,
}

function ApiCommentsTable({ commentsReducer, userReducer, apiReducer, deleteComment, editComment }: ApiCommentsTableProps) {
    const intl = useIntl();
    const classes = useStylesCommentsTable();
    const { loading: commentLoading, comments } = commentsReducer;
    const { apimTokenObtained, userInfo, selectedOrg } = userReducer;
    const { api } = apiReducer;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [commentsState, setCommentsState] = useState(comments);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [deleteCommentId, setDeleteCommentId] = useState("");
    const rows: Comment[] = commentsState.list;

    useEffect(() => {
        if (isValidData(comments)) {
            setCommentsState(comments);
        }
    }, [commentsReducer]);

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeletePopoverClose = () => {
        setDeleteCommentId("");
        setAnchorEl(null);
    };
    const handleDeletePopoverClick = (event: React.MouseEvent<HTMLButtonElement>, commendId: string) => {
        setDeleteCommentId(commendId);
        setAnchorEl(event.currentTarget);
    };

    const handleDeleteComment = (event: React.MouseEvent<HTMLButtonElement>) => {
        const deleteCommentId = event.currentTarget.id;
        if (api.id && deleteCommentId && selectedOrg.uuid) {
            setDeleteCommentId("");
            setAnchorEl(null);
            deleteComment(api.id, selectedOrg.uuid, deleteCommentId);
        }
    }
    const handleEditCommentFormSubmit = async (value: string, commentId: string) => {
        const fullName = userInfo[CLAIMS_FULL_NAME] || 'Anonymous';
        const data: CreateComment = {
            content: value + CREATED_BY_USER + fullName,
        };
        if (api.id && selectedOrg.uuid) {
            editComment(api.id, selectedOrg.uuid, commentId, data);
        }
    };

    return (
        commentLoading ?
            renderSkeleton("rect", "100%", 100)
            :
            comments.count === 0 ?
                <i data-testid="txt-no-comments">
                    <FormattedMessage
                        id="api.apiOverview.apiCommentsTable.messages.noCommentsAvailable"
                        defaultMessage="No comments available"
                    />
                </i>
                :
                <Table
                    data-testid="table-comments"
                    className={classes.table}
                    aria-label="custom pagination table">
                    <TableBody>
                        {(rowsPerPage > 0
                            ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : rows
                        ).map((row: Comment) => {
                            // @TODO Appending the full name of the commentor as a temporary fix to overcome UUID username.
                            let commentContent = "";
                            let commentCreator = "";
                            if (row.content?.includes(Constants.CREATED_BY_USER)) {
                                [commentContent, commentCreator] = row.content.split(Constants.CREATED_BY_USER);
                            } else {
                                commentContent = row.content;
                                commentCreator = intl.formatMessage({
                                    id: "api.apiOverview.apiCommentsTable.labels.anonymousCreator",
                                    defaultMessage: "Anonymous"
                                });
                            }
                            const deleteButtonDisplay = commentCreator === userInfo[Constants.CLAIMS_FULL_NAME];

                            return (
                                <TableRow key={row.id}>
                                    <TableCell component="th" scope="row" className={classes.tableCellContent}>
                                        <Box>
                                            <Typography variant='body1' component="div">
                                                <EditableTextField
                                                    placeholder=""
                                                    isEditable={apimTokenObtained}
                                                    value={commentContent}
                                                    onAction={(value: string) => handleEditCommentFormSubmit(value, row.id)}
                                                    multiline
                                                />
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCell} align="right">
                                        {commentCreator}
                                        <br />
                                        <span style={{ color: '#CBCEDB' }}>{getTimeDurationForDate(row.createdTime)}</span>
                                        {
                                            deleteButtonDisplay &&
                                            (<div style={{ display: 'inline' }}>
                                                <IconButton
                                                    data-testid="btn-delete-comment"
                                                    disableRipple
                                                    aria-label="delete"
                                                    className={classes.commentDeleteBtn}
                                                    onClick={(event) => handleDeletePopoverClick(event, row.id)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" style={{ color: "#EA4C4D" }} />
                                                </IconButton>
                                                <Popover
                                                    data-testid="popup-delete-comment-confirm"
                                                    id={row.id}
                                                    open={deleteCommentId === row.id}
                                                    anchorEl={anchorEl}
                                                    onClose={handleDeletePopoverClose}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'center',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'center',
                                                    }}
                                                >
                                                    <Typography className={classes.cmntDelConfirmText}>
                                                        <FormattedMessage
                                                            id="api.apiOverview.apiCommentsTable.buttons.deleteComment"
                                                            defaultMessage="Are you sure you want to delete this comment?"
                                                        />
                                                    </Typography>
                                                    <PrimaryButton
                                                        data-testid="btn-delete-comment-reject"
                                                        buttonLabel="No"
                                                        color="default"
                                                        className={classes.commentDelConfirm}
                                                        onClick={handleDeletePopoverClose}
                                                    />
                                                    <PrimaryButton
                                                        data-testid="btn-delete-comment-approve"
                                                        id={row.id}
                                                        buttonLabel="Yes"
                                                        color="primary"
                                                        className={classes.commentDelConfirm}
                                                        onClick={handleDeleteComment}
                                                    />
                                                </Popover>
                                            </div>
                                            )
                                        }
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                colSpan={3}
                                count={rows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: { 'aria-label': 'rows per page' },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        commentsReducer: state.apiCommentsReducer,
        userReducer: state.userReducer,
        apiReducer: state.apiReducer,
    }
}

const mapDispatchToProps = {
    deleteComment: deleteComment,
    editComment: editComment,
}

export default connect(mapStateToProps, mapDispatchToProps)(ApiCommentsTable);
